import { takeLatest, put, call, select, all } from 'redux-saga/effects';
import get from 'lodash/get';
import {
  getOfficesAction,
  getArtistsAction,
  getAgencyAgreementsAction,
  getAgencyInfoAction,
  deleteArtistFromRosterAction,
  addArtistToRosterAction,
  getUserProfileAgentInfoAction,
  checkArtistInAgencyAction,
  getSingleArtistInfoAction,
  getAgencyCommonInfoAction,
  getAgentsAction,
  getArtistsListSuggestionsAction,
  updateAgencyAction,
  updateArtistAction,
} from './actions';
import {
  getOffices,
  getAgencyArtists,
  getAgency,
  getArtistsAgreements,
  removeArtistFromRoster,
  addArtistToAgencyRoster,
  getAgencyAgents,
  deleteArtistAgreement,
  createArtistsAgreement,
  updateArtistAgreement,
  searchProfiles,
  getAgencyArtist,
  updateAgency,
  deleteAgencyOffice,
  updateAgencyOffice,
  createAgencyOffice,
  updateAgencyArtist,
  createUserProfile,
} from '../../utils/API';
import { selectUserProfileAgentInfo } from './selectors';
import { selectActiveProfile } from '../App/selectors';
import { ARTIST_TYPE_ID, VISIBILITY } from '../../constants/consts';
import { enqueueSnackbarAction } from '../App/actions';

export function* fetchAgencyInfo({ payload }) {
  try {
    const agencyId = get(payload, 'agencyId', '');
    const query = get(payload, 'query', {});
    yield call(fetchOffices, { payload: { agency_id: agencyId, ...query } });
    yield call(fetchArtists, { payload: { agencyId, query } });
  } catch (err) {
    yield put(getAgencyInfoAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: `Error fetch agency data: ${err}`,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getAgencyInfoAction.fulfill());
  }
}

export function* fetchAgencyCommonInfo({ payload }) {
  try {
    const agencyId = get(payload, 'agencyId', '');
    const query = get(payload, 'query', {});
    const agencyInfo = yield call(getAgency, agencyId, query);
    const agencyData = get(agencyInfo, 'data');
    yield put(getAgencyCommonInfoAction.success(agencyData));
  } catch (err) {
    yield put(getAgencyCommonInfoAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: `Error fetch agency info: ${err}`,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getAgencyCommonInfoAction.fulfill());
  }
}

export function* fetchOffices({ payload }) {
  try {
    const response = yield call(getOffices, payload);
    yield put(getOfficesAction.success(get(response, 'data.data', [])));
  } catch (err) {
    yield put(getOfficesAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: `Error fetch offices: ${err}`,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getOfficesAction.fulfill());
  }
}

export function* fetchArtists({ payload }) {
  try {
    const response = yield call(getAgencyArtists, get(payload, 'agencyId'), { ...get(payload, 'query'), limit: 1000 });
    const artists = get(response, 'data.data', []);
    yield put(getArtistsAction.success(artists));
    const ids = artists.map(artist => get(artist, 'id'));
    yield call(fetchAgencyAgreements, { payload: { ids } });
  } catch (err) {
    yield put(getArtistsAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: `Error fetch artist: ${err}`,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getArtistsAction.fulfill());
  }
}

export function* fetchAgencyAgreements({ payload }) {
  try {
    const response = yield call(getArtistsAgreements, get(payload, 'ids'), { limit: 1000 });
    yield put(getAgencyAgreementsAction.success(get(response, 'data.data', [])));
  } catch (err) {
    yield put(getAgencyAgreementsAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: `Error fetch agreements: ${err}`,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getAgencyAgreementsAction.fulfill());
  }
}

export function* fetchAgencyAgents({ payload }) {
  try {
    const agencyId = get(payload, 'agencyId');
    const response = yield call(getAgencyAgents, agencyId, get(payload, 'query'));
    yield put(getAgentsAction.success(get(response, 'data.data', [])));
  } catch (err) {
    yield put(getAgentsAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: `Error fetch agents: ${err}`,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getAgentsAction.fulfill());
  }
}

export function* deleteArtistFromRoster({ payload }) {
  try {
    const agencyId = get(payload, 'agencyId');
    yield call(removeArtistFromRoster, agencyId, get(payload, 'artistId'));
    yield put(deleteArtistFromRosterAction.success());
    yield call(fetchArtists, { payload: { agencyId } });
  } catch (err) {
    yield put(deleteArtistFromRosterAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: `Error delete artist: ${err}`,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(deleteArtistFromRosterAction.fulfill());
  }
}

export function* addArtistToRoster({ payload }) {
  try {
    const agentInfo = yield select(selectUserProfileAgentInfo());
    const activeProfile = yield select(selectActiveProfile());
    const artistExists = !!payload?.artist?.profile?.id;
    let artistData = get(payload, 'artist');
    if (!artistExists) {
      const createdArtistProfile = yield call(createUserProfile, {
        ...artistData.profile,
        profileType: { id: ARTIST_TYPE_ID },
      });
      const profileData = get(createdArtistProfile, 'data');
      const qualificationId = artistData?.profile?.profession?.professionQualification?.id;
      artistData = {
        profile: {
          id: profileData?.id,
          profession: {
            id: profileData?.profession?.id,
            ...(qualificationId ? { professionQualification: { id: qualificationId } } : {}),
          },
        },
      };
    }
    const response = yield call(addArtistToAgencyRoster, get(payload, 'agencyId'), get(activeProfile, 'id'), {
      ...(get(agentInfo, 'id')
        ? {
            agent: {
              id: get(agentInfo, 'id'),
              ...(get(agentInfo, 'agent.profile.id') ? { profile: { id: get(agentInfo, 'agent.profile.id') } } : {}),
            },
          }
        : {}),
      artist: artistData,
    });
    yield put(addArtistToRosterAction.success(get(response, 'data')));
    const callbackFunc = get(payload, 'callback');
    if (callbackFunc && typeof callbackFunc === 'function') {
      callbackFunc();
    }
    yield put(
      enqueueSnackbarAction({
        message: `Success: artist is added to the roster`,
        options: {
          variant: 'success',
        },
      }),
    );
  } catch (err) {
    yield put(addArtistToRosterAction.failure(err));
    yield put(
      enqueueSnackbarAction({
        message: `Error adding artist into roster: ${err}`,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(addArtistToRosterAction.fulfill());
  }
}

export function* fetchAgentInfo({ payload }) {
  try {
    const agencyId = get(payload, 'agencyId');
    const profileId = get(payload, 'profileId');
    let agentInfoResponse = {};
    if (profileId) {
      agentInfoResponse = yield call(getAgencyAgents, agencyId, {
        profile_id: get(payload, 'profileId'),
        all: true,
      });
    }
    yield put(getUserProfileAgentInfoAction.success(get(agentInfoResponse, 'data.data[0]', {})));
  } catch (err) {
    yield put(getUserProfileAgentInfoAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: `Error fetch agent info: ${err}`,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getUserProfileAgentInfoAction.fulfill());
  }
}

export function* isArtistInRoster({ payload }) {
  try {
    const agencyId = get(payload, 'agencyId');
    const artistInfoResponse = yield call(getAgencyArtists, agencyId, {
      artist_profile_id: get(payload, 'profileId'),
    });
    yield put(checkArtistInAgencyAction.success(get(artistInfoResponse, 'data.data[0]', null)));
  } catch (err) {
    yield put(checkArtistInAgencyAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: `Error fetch artist in roster: ${err}`,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(checkArtistInAgencyAction.fulfill());
  }
}

export function* getArtistInfo({ payload }) {
  try {
    const agencyId = get(payload, 'agencyId');
    const artistId = get(payload, 'artistId');
    const artistInfoResponse = yield call(getAgencyArtist, agencyId, artistId, get(payload, 'query'));

    const artistAgreementsResponse = yield call(getArtistsAgreements, [artistId], get(payload, 'query'), {
      limit: 100,
    });

    yield put(
      getSingleArtistInfoAction.success({
        ...get(artistInfoResponse, 'data'),
        agreements: get(artistAgreementsResponse, 'data.data', []),
      }),
    );
  } catch (err) {
    yield put(getSingleArtistInfoAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: `Error fetch artist info: ${err}`,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getSingleArtistInfoAction.fulfill());
  }
}

export function* getArtistsSuggestionsList({ payload }) {
  try {
    const agencyId = get(payload, 'agencyId');
    const response = yield call(
      searchProfiles,
      ARTIST_TYPE_ID,
      get(payload, 'query'),
      {},
      { show_invalid: true, visibility: VISIBILITY.ALL },
    );
    const responseData = get(response, 'data.data', []);
    let artistsInfoResponse = [];
    if (responseData.length > 0) {
      artistsInfoResponse = yield call(getAgencyArtists, agencyId, {
        artist_profile_id: responseData.map(artist => get(artist, 'id')),
      });
    }
    yield put(
      getArtistsListSuggestionsAction.success({
        artists: responseData,
        agencyArtists: get(artistsInfoResponse, 'data.data', []),
      }),
    );
  } catch (err) {
    yield put(getArtistsListSuggestionsAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: `Error get artist suggestions: ${err}`,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(getArtistsListSuggestionsAction.fulfill());
  }
}

export function* updateAgencyData({ payload }) {
  try {
    const { agencyId, profileId, agencyData, deletedOffices, updatedOffices, createdOffices, callback } = payload;
    yield all([
      call(updateAgency, agencyId, get(agencyData, 'data'), get(agencyData, 'query')),
      ...deletedOffices.map(id => call(deleteAgencyOffice, id, agencyId, profileId)),
      ...updatedOffices.map(office =>
        call(updateAgencyOffice, get(office, 'id'), agencyId, profileId, get(office, 'data')),
      ),
      ...createdOffices.map(office => call(createAgencyOffice, agencyId, profileId, get(office, 'data'))),
    ]);

    if (callback && typeof callback === 'function') {
      callback();
    }
  } catch (err) {
    yield put(updateAgencyAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: `Error updating agency: ${err}`,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(updateAgencyAction.fulfill());
  }
}

export function* updateArtistData({ payload }) {
  try {
    const {
      profileId,
      artistId,
      artistData,
      deletedAgreements,
      updatedAgreements,
      createdAgreements,
      callback,
    } = payload;
    yield all([
      call(
        updateAgencyArtist,
        get(artistData, 'agencyId'),
        get(artistData, 'artistId'),
        get(artistData, 'data'),
        get(artistData, 'query'),
      ),
      ...updatedAgreements.map(agreement => call(updateArtistAgreement, artistId, agreement, profileId)),
      ...deletedAgreements.map(agreementIdToDelete => call(deleteArtistAgreement, agreementIdToDelete, profileId)),
      ...createdAgreements.map(agr => call(createArtistsAgreement, artistId, agr, profileId)),
    ]);

    if (callback && typeof callback === 'function') {
      callback();
    }
    yield put(updateArtistAction.success());
  } catch (err) {
    yield put(updateArtistAction.failure(err.message));
    yield put(
      enqueueSnackbarAction({
        message: `Error update artist data: ${err}`,
        options: {
          variant: 'error',
        },
      }),
    );
  } finally {
    yield put(updateArtistAction.fulfill());
  }
}

export default function* dataAgencies() {
  yield takeLatest(getAgencyInfoAction.TRIGGER, fetchAgencyInfo);
  yield takeLatest(getAgencyCommonInfoAction.TRIGGER, fetchAgencyCommonInfo);
  yield takeLatest(getOfficesAction.TRIGGER, fetchOffices);
  yield takeLatest(getAgentsAction.TRIGGER, fetchAgencyAgents);
  yield takeLatest(deleteArtistFromRosterAction.TRIGGER, deleteArtistFromRoster);
  yield takeLatest(addArtistToRosterAction.TRIGGER, addArtistToRoster);
  yield takeLatest(getUserProfileAgentInfoAction.TRIGGER, fetchAgentInfo);
  yield takeLatest(checkArtistInAgencyAction.TRIGGER, isArtistInRoster);
  yield takeLatest(getSingleArtistInfoAction.TRIGGER, getArtistInfo);
  yield takeLatest(getArtistsListSuggestionsAction.TRIGGER, getArtistsSuggestionsList);
  yield takeLatest(updateArtistAction.TRIGGER, updateArtistData);
  yield takeLatest(updateAgencyAction.TRIGGER, updateAgencyData);
}
